/**
 * Запрос местоположения при входе в /dashboard (PROJECT_NOTES §37): если у аптеки нет сохраненной точки,
 * кабинет открывается только после мастера. Проверка раз за сессию вкладки - тем же флагом sessionStorage, что
 * и у старого GeolocationManager кабинета: после ворот он видит «уже проверено» и свой QR-диалог не показывает.
 * Вошел администратор под чужим логином или проверка не удалась - кабинет открывается без запроса.
 */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PinDropOutlined } from '@mui/icons-material';
import { notify } from '../notifications';
import { fetchSavedGeo, profileGeoBackend, SavedPoint } from './geoApi';
import GeoWizard from './GeoWizard';
import PhoneHandoffPanel from './PhoneHandoffPanel';
import { cardSx, hintSx, iconBoxSx, TEXT } from './geoStyles';

const CHECKED_KEY = 'geolocation_checked';

type GateState = 'checking' | 'required' | 'passed';

// Хранилище может быть недоступно (приватный режим, запрет сайта): тогда спрашиваем сервер при каждом входе
function wasChecked(): boolean {
  try {
    return sessionStorage.getItem(CHECKED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markChecked(): void {
  try {
    sessionStorage.setItem(CHECKED_KEY, 'true');
  } catch {
    // без флага ворота просто проверят точку еще раз при следующем открытии
  }
}

const pageSx = { minHeight: '100vh', bgcolor: '#f8fafc' };

const FirstLoginGeoGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<GateState>(() => (wasChecked() ? 'passed' : 'checking'));

  useEffect(() => {
    if (state !== 'checking') return undefined;
    let cancelled = false;
    fetchSavedGeo()
      .then(({ geolocation, impersonated }) => {
        if (cancelled) return;
        if (geolocation || impersonated) {
          markChecked();
          setState('passed');
        } else {
          setState('required');
        }
      })
      // Как и раньше в кабинете: сбой проверки не закрывает работу аптеки. Флаг не ставим - пусть старый
      // GeolocationManager проверит сам, если наш эндпоинт недоступен (например, бэкенд еще не перезапущен)
      .catch(() => { if (!cancelled) setState('passed'); });
    return () => { cancelled = true; };
  }, [state]);

  const handleSaved = (geo: SavedPoint, message: string) => {
    markChecked();
    if (geo.address) notify.success(message);
    else notify.warning(message);
    setState('passed');
  };

  if (state === 'passed') return <>{children}</>;

  if (state === 'checking') {
    return (
      <Box sx={{ ...pageSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...pageSx, px: 2, py: { xs: 3, sm: 5 } }}>
      <Box sx={{ maxWidth: 760, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
          <Box sx={iconBoxSx}><PinDropOutlined /></Box>
          <Box>
            <Typography component="h1" sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: '1.15rem', sm: '1.3rem' }, lineHeight: 1.3 }}>
              Укажите местоположение аптеки
            </Typography>
            <Typography sx={{ ...hintSx, mt: 0.5 }}>
              У аптеки еще нет точки на карте. Это нужно сделать один раз, займет около минуты.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 3 } }}>
          <GeoWizard
            backend={profileGeoBackend}
            initial={null}
            onSaved={handleSaved}
            renderPhone={back => <PhoneHandoffPanel onSaved={handleSaved} onBack={back} />}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FirstLoginGeoGate;
